"use client";
import React, {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import clsx from "clsx";
import { useLocale } from "next-intl";

import {
  getDefaultLocale,
  getFilteredLanguageTabs,
  getSupportedLocalesArray,
  isSupportedLocale,
} from "@/config/locale.config";
import { SupportedLocale } from "@/enums/locale.enum";
import BaseInput, { BaseInputProps } from "@/ui/Forms/BaseInput";

// Use SupportedLocale instead of custom SupportedLanguage
export type SupportedLanguage = SupportedLocale;

// Structure for multi-lingual values - now dynamic based on country config
export interface MultiLingualValue {
  [key: string]: string | undefined;
}

// Error format from react-hook-form
export interface FieldError {
  type: string;
  message: string;
}

// New error types
export type MultiLingualErrors =
  | {
      [key: string]: FieldError;
    }
  | Record<string, never>
  | undefined;

// onBlur handler type
export type OnBlurHandler = (
  lang: string,
) => (event: React.FocusEvent<HTMLInputElement>) => void;

export interface FormMultiLingualInputProps
  extends Omit<BaseInputProps, "onChange" | "value" | "error" | "onBlur"> {
  value: MultiLingualValue;
  onChange?: (value: MultiLingualValue) => void;
  languages?: SupportedLanguage[];
  mode?: "edit" | "readonly";
  synchronizedLanguage?: SupportedLanguage;
  onLanguageChange?: (language: SupportedLanguage) => void;
  error?: MultiLingualErrors | string;
  fieldName?: string;
  onBlur?: OnBlurHandler;
  clearErrors?: (fieldPath: string | string[]) => void;
}

const FormMultiLingualInput = (
  {
    value,
    onChange,
    label,
    error,
    insideRightComponent,
    labelRightComponent,
    required = false,
    languages, // Will use country config if not provided
    mode = "edit",
    synchronizedLanguage,
    onLanguageChange,
    className,
    errorClassName,
    fieldName,
    onBlur,
    clearErrors,
    ...inputProps
  }: FormMultiLingualInputProps,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const locale = useLocale();
  const currentLocale = isSupportedLocale(locale) ? locale : getDefaultLocale();

  // Get supported languages from country config if not provided
  const supportedLanguages = languages || getSupportedLocalesArray();

  const [languageErrors, setLanguageErrors] = useState<Record<string, string>>(
    {},
  );

  const [activeLanguage, setActiveLanguage] = useState<SupportedLanguage>(
    () => {
      // Ensure we only use supported languages from country config
      const validLanguages = supportedLanguages;

      // Debug information - remove in production
      if (process.env.NODE_ENV === "development") {
        console.log("FormMultiLingualInput debug:", {
          supportedLanguages,
          validLanguages,
          currentLocale,
        });
      }

      return validLanguages.includes(currentLocale)
        ? currentLocale
        : validLanguages[0] || getDefaultLocale();
    },
  );

  const getLanguageErrors = useCallback(
    (
      rawError: MultiLingualErrors | undefined | string,
      currentFieldName: string,
    ): Record<string, string> => {
      if (!rawError) {
        return {};
      }

      if (typeof rawError === "string") {
        return { [activeLanguage]: rawError };
      }

      const errors: Record<string, string> = {};
      const pattern = new RegExp(
        `^${currentFieldName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\.(\\w+)$`,
      );

      Object.entries(rawError).forEach(([key, value]) => {
        const match = key.match(pattern);
        if (match && value && typeof value === "object" && "message" in value) {
          const lang = match[1];
          if (typeof value.message === "string") {
            errors[lang] = value.message;
          }
        }
      });
      return errors;
    },
    [activeLanguage],
  );

  const serverErrorsSerializedRef = useRef<string>("");

  useEffect(() => {
    if (fieldName) {
      setLanguageErrors(getLanguageErrors(error, fieldName));
    }
  }, [error]);

  // Get current error message for active language
  const getCurrentErrorMessage = () => {
    if (typeof error === "string") {
      return error;
    }
    return languageErrors[activeLanguage];
  };

  // Check if there are any errors in any language
  const hasErrors = Object.keys(languageErrors).length > 0;

  // Update active language if synchronizedLanguage changed
  useEffect(() => {
    if (synchronizedLanguage) {
      // Only allow supported languages from country config
      const validLanguages = getSupportedLocalesArray();
      const safeLanguage = validLanguages.includes(synchronizedLanguage)
        ? synchronizedLanguage
        : getDefaultLocale();

      if (supportedLanguages.includes(safeLanguage)) {
        setActiveLanguage(safeLanguage);
      }
    }
  }, [synchronizedLanguage, supportedLanguages]);

  // Get filtered language tabs based on available languages
  const availableTabs = getFilteredLanguageTabs(supportedLanguages);

  const handleLanguageChange = (language: SupportedLanguage) => {
    setActiveLanguage(language);

    // Notify parent about language change
    if (onLanguageChange) {
      onLanguageChange(language);
    }

    // Clear previous errors and immediately re-validate the currently
    // active language so that the UI always reflects the latest state.
    // if (clearErrors && fieldName) {
    //   clearErrors([`${fieldName}.${language}`, fieldName]);
    //   const triggerFn = (
    //     clearErrors as unknown as {
    //       form?: { trigger?: (name: string | string[]) => void };
    //     }
    //   )?.form?.trigger;
    //   triggerFn?.([`${fieldName}.${language}`, fieldName]);
    // }

    // if (languageErrors[language]) {
    //   setLanguageErrors((prev) => {
    //     const newErrors = { ...prev };
    //     delete newErrors[language];
    //     return newErrors;
    //   });
    // }
  };

  const handleInputChange = (lang: SupportedLanguage, inputValue: string) => {
    const currentValues = value || {};
    const newValue = { ...currentValues, [lang]: inputValue };
    if (onChange) {
      onChange(newValue);
    }

    // If there was an error for this language, clear it from local state for immediate UI feedback
    if (languageErrors[lang]) {
      setLanguageErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[lang];
        return newErrors;
      });
    }

    // Also attempt to clear it in react-hook-form
    if (clearErrors && fieldName) {
      clearErrors([`${fieldName}.${lang}`, fieldName]);
    }
  };

  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(activeLanguage)(e);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [activeLanguage, onBlur],
  );

  // If mode is readonly, display component in ReadOnlyMultiLingualInput style

  // Standard editing mode
  return (
    <div className="w-full relative">
      {/* Language tabs */}
      <div className="flex mb-[1rem] justify-end absolute top-[-0.5rem] z-[1] right-0">
        {availableTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={clsx(
              "py-[0.8rem] px-[1.5rem] bg-none text-[1.4rem] font-medium text-gray-1300 cursor-pointer",
              "transition-all duration-300 ease-in-out border-b-2 border-transparent border-solid",
              activeLanguage === tab.key
                ? "text-red border-b-2 border-b-red"
                : "hover:text-input-text",
              languageErrors[tab.key] ? "text-red" : "",
            )}
            onClick={() => handleLanguageChange(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Input field */}
      <BaseInput
        ref={ref}
        label={label}
        error={getCurrentErrorMessage()}
        required={required}
        labelRightComponent={labelRightComponent}
        value={(value && value[activeLanguage]) || ""}
        onChange={(e) => handleInputChange(activeLanguage, e.target.value)}
        disabled={mode === "readonly"}
        insideRightComponent={insideRightComponent}
        className={className}
        errorClassName={errorClassName}
        onBlur={handleInputBlur}
        {...inputProps}
      />
    </div>
  );
};

export default forwardRef(FormMultiLingualInput);
